import { internalAxios } from '@/plugins/vueAxios'
import { defineStore } from 'pinia'
import type { GalleryCollectionsApiResponse } from '@/interfaces/responses/gallery/GalleryCollectionsApiResponse'
import {
  galleryCollectionsEndpoint as GALLERY_COLLECTIONS_ENDPOINT,
  galleryCollectionEndpoint as GALLERY_COLLECTION_ENDPOINT,
  galleryCollectionClaimEndpoint as COLLECTION_CLAIM_ENDPOINT,
  galleryPicturesEndpoint as SPECIAL_PICTURES_ENDPOINT,
} from '@/globalVariables'
import type { GalleryCollection } from '@/interfaces/gallery/Collections'
import type { GalleryPicture } from '@/interfaces/gallery/Pictures'
import { GalleryCollectionState, GalleryPictureState } from '@/interfaces/gallery/enums'
import { useResponseTaskStore } from '../responseTaskStore'

interface GalleryPacksState {
  collections: GalleryCollection[] | null
  collection: GalleryCollection | null
  repeatablePictures: GalleryPicture[] | null
  specialPictures: GalleryPicture[] | null
}

export const useGalleryCollectionsStore = defineStore('galleryCollections', {
  state: (): GalleryPacksState => ({
    collections: null,
    collection: null,
    repeatablePictures: null,
    specialPictures: null,
  }),

  getters: {
    collectionsCompleted(): number {
      return (
        this.collections?.filter(
          (collection: GalleryCollection): boolean =>
            collection.state === GalleryCollectionState.CompletedClaimed ||
            collection.state === GalleryCollectionState.CompletedUnclaimed,
        ).length ?? 0
      )
    },

    repeatableCompleted(): number {
      if (!this.repeatablePictures) return 0
      return this.repeatablePictures.reduce(
        (sum: number, picData: GalleryPicture) => (sum += picData.finished_times || 0),
        0,
      )
    },

    specialCompleted(): number {
      return (
        this.specialPictures?.filter(
          (pic: GalleryPicture): boolean =>
            pic.state === GalleryPictureState.CompletedClaimed ||
            pic.state === GalleryPictureState.CompletedUnclaimed,
        ).length ?? 0
      )
    },
  },

  actions: {
    async loadCollections(): Promise<void> {
      this.collections = null

      try {
        const data = await internalAxios.get<{}, GalleryCollectionsApiResponse>(
          GALLERY_COLLECTIONS_ENDPOINT,
        )

        if (data === null) return

        this.collections = data.collections

        if (data.notifications_collections || data.notifications_packs) {
          useResponseTaskStore().updateGalleryNotifications({
            collections: data.notifications_collections || 0,
            packs: data.notifications_packs || 0,
          })
        }
      } catch (error: unknown) {
        console.error(error)
      }
    },

    async loadRepeatable(): Promise<void> {
      const endpoint = `${SPECIAL_PICTURES_ENDPOINT}repeatable`

      try {
        const data = await internalAxios.get<{}, GalleryPicture[]>(endpoint)

        if (data === null) return

        this.repeatablePictures = data
      } catch (error: unknown) {
        console.error(error)
      }
    },

    async loadSpecial(): Promise<void> {
      const endpoint = `${SPECIAL_PICTURES_ENDPOINT}special`

      try {
        const data = await internalAxios.get<{}, GalleryPicture[]>(endpoint)

        if (data === null) return

        this.specialPictures = data
      } catch (error: unknown) {
        console.error(error)
      }
    },

    async loadCollection(id: string): Promise<void> {
      try {
        this.collection = null
        const data = await internalAxios.get<{}, GalleryCollection>(
          `${GALLERY_COLLECTION_ENDPOINT}${id}`,
        )

        if (data === null) return

        this.collection = data
      } catch (error: unknown) {
        console.error(error)
      }
    },

    reloadCollection(): void {
      if (this.collection?.id) this.loadCollection(this.collection.id)
    },

    async claimCollectionReward(collection_id: string): Promise<void> {
      if (!collection_id) return

      try {
        const response = await internalAxios.post<{}, GalleryCollection, { collection_id: string }>(
          COLLECTION_CLAIM_ENDPOINT,
          { collection_id },
        )

        if (!response) return
        this.collection = response
      } catch (error: unknown) {
        console.error(error)
      }
    },

    // except given picture ID
    // are all other pictures in this collection complete?
    isCollectionCompleted(lastIncompletePicId: string): boolean {
      return !!this.collection?.pictures?.every(
        (pic: GalleryPicture): boolean =>
          pic.state === GalleryPictureState.CompletedClaimed ||
          pic.state === GalleryPictureState.CompletedUnclaimed ||
          pic.id === lastIncompletePicId,
      )
    },

    getCollectionIdByName(collectionName: string): string {
      return (
        this.collections?.find(
          (coll: GalleryCollection): boolean => coll.parameters.name === collectionName,
        )?.id || ''
      )
    },

    // update collection data if the last picture was completed
    checkCollectionAfterPictureComplete(collectionName: string, completedPicId: string): void {
      // if this collection is in store
      // check if the collection should be complete now => reload it
      // do nothing if there are more incomplete pictures in the collection
      if (
        this.collection?.parameters?.name === collectionName &&
        this.isCollectionCompleted(completedPicId)
      ) {
        this.reloadCollection()
        return
      }

      // collection is not in store
      // so load it => we need collection ID
      const collectionId = this.getCollectionIdByName(collectionName)
      if (collectionId) this.loadCollection(collectionId)
    },
  },
})
